import React, { useRef, useCallback, useMemo, useEffect, useState, useContext, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import COMMON from "common";
import pathnames from "routes/pathnames";
import api from "services/api";
import ERRORS from "common/errors";
import sanitizeObject from "common/sanitize-object";
import { formatDate, CALENDAR_FORMAT, isDateBeforeToday, formatDateAndTime, formatDateTime, isEndDateBeforeStartDate } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import queryParamsEntries from "common/query-params-entries";
import capitalizeCharacter from "common/capitalize-character";
import { AxiosContext } from "contexts/with-interceptor-provider";
import AppStatus from "components/app-status";
import AppCheckbox from "components/app-checkbox";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppConfirmDeleteModal from "components/pages/leave-management/app-confirm-delete-leave";
import AppInput from "components/app-input";
import AppCalendarInput from "components/app-calendar-input";
import AppButton from "components/app-button";
import AppSelectInput from "components/app-select-input";
import AppSearchInput from "components/app-search-input";
import pdfIcon from "assets/images/pdf-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import exportIcon from "assets/images/pages/user-management/export-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import placeholderFormat from "common/placeholder-format";
import viewIcon from "assets/images/view-icon.svg";
import AppLeaveModal from "./app-leave-modal";


const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const AppAllLeaveHistory = () => {
    const tableRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const navigate = useNavigate();
    const searchInputRef = useRef();
    const confirmDeleteRef = useRef();
    const viewRef = useRef();
    const context = useContext(AxiosContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLeave, setselectedLeave] = useState({});
    const memoSetSearchParams = useRef(setSearchParams);
    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1,
        sort: searchParams.get("sort") || "",
        name: searchParams.get("name") || "",
        nickName: searchParams.get("nickName") || "",
        employeeId: searchParams.get("employeeId") || "",
        email: searchParams.get("email") || "",
        statusListStr: searchParams.get("statusListStr") || "",
        leaveType: searchParams.get("leaveType") || "",
        startDate: searchParams.get("startDate") || "",
        endDate: searchParams.get("endDate") || "",
        createdStartDate: searchParams.get("createdStartDate") || "",
        createdEndDate: searchParams.get("createdEndDate") || "",
    });
    const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

    const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status && !paramsRef.current.employeeId && !paramsRef.current.email && !paramsRef.current.name && !paramsRef.current.statusListStr && !paramsRef.current.leaveType && !paramsRef.current.startDate && !paramsRef.current.endDate && !paramsRef.current.createdStartDate && !paramsRef.current.createdEndDate && !paramsRef.current.nickName, [data.status]);

    const initialValues = useMemo(() => ({ employeeId: "", name: "", nickName: "", email: "", statusListStr: "", leaveType: "", startDate: "", endDate: "", createdStartDate: "", createdEndDate: "" }), []);
    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            startDate: yup.string().nullable().test("beforeEndDate", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.endDate) return true;
                return isDateBeforeToday(value, this.parent.endDate);
            }),
            endDate: yup.string().nullable().test("afterStartDate", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.startDate) return true;
                return isDateBeforeToday(this.parent.startDate || new Date(), value);
            }),
            createdStartDate: yup.string().nullable().test("beforeEndDate", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.createdEndDate) return true;
                return isDateBeforeToday(value, this.parent.createdEndDate);
            }),
            createdEndDate: yup.string().nullable().test("afterStartDate", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.createdStartDate) return true;
                return isDateBeforeToday(this.parent.createdStartDate || new Date(), value);
            }),
        }),
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    //prettier-ignore
    const onHandleStartDate = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.endDate)) formik.setFieldValue("endDate", "");
    }, [formik]);

    //prettier-ignore
    const onHandleCreatedStartDate = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.createdEndDate)) formik.setFieldValue("createdEndDate", "");
    }, [formik]);

    const setValues = useMemo(() => formik.setValues, [formik]);

    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        let values = { statusListStr: capitalizeCharacter(params.statusListStr?.split("_")?.join(" ")), name: params.name, nickName: params.nickName, email: params.email, employeeId: params.employeeId, leaveType: capitalizeCharacter(params.leaveType?.split("_")?.join(" ")), startDate: params.startDate, endDate: params.endDate, createdStartDate: params.createdStartDate, createdEndDate: params.createdEndDate };

        return values;
    }, [searchParams]);

    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");

        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };

        onHandleGetList();
    };

    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);

        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };

        const columnIds = columns.map((col) => col.id).filter((col) => defaultNoDisplayHeaders(col));
        setSelectedColumnIds(columnIds);
        setExportColumnIds(columnIds);

        onHandleGetList();
    };

    const onHandleSubmitSearch = (values) => {
        searchInputRef.current.onhandleCloseAdvanceSearch();

        paramsRef.current = { ...paramsRef.current, page: 1, ...values };

        onHandleGetList();
    };

    const onHandleShowAdvanceSearch = (event) => {
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload));

            payload.page = paramsRef.current.page - 1;

            if (payload.startDate) {
                payload.startDate = formatDateAndTime(new Date(payload.startDate));
            }

            if (payload.endDate) {
                payload.endDate = formatDateAndTime(new Date(payload.endDate));
            }

            if (payload.createdStartDate) {
                payload.createdStartDate = formatDateAndTime(new Date(payload.createdStartDate));
            }

            if (payload.createdEndDate) {
                payload.createdEndDate = formatDateAndTime(new Date(payload.createdEndDate));
            }

            response = await api.get.leaves.list(sanitizeObject({ ...payload }));
        } catch (error) {
            serveRequestErrors(error);
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    const onHandleView = () => {
		setAnchorEl(null);
		viewRef.current.onHandleShow(selectedLeave.id);
	};

    const onHandleRemove = () => {
		setAnchorEl(null);
		confirmDeleteRef.current.onHandleShow(selectedLeave.id);
	};

    const onHandleCloseMenu = () => {
		setAnchorEl(null);
	};

    const onHandleTableMenu = (event, { row }) => {
		setselectedLeave(row.original);
		setAnchorEl(event.currentTarget);
	};

    const MenuCell = useCallback((obj) => {
        return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, obj)} />;
    }, []);

    const StartDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row.original.companyLeaveApplicationDetails[0].date) || "-"} />, []);

    const EndDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row?.original?.companyLeaveApplicationDetails?.[row?.original?.companyLeaveApplicationDetails?.length - 1]?.date) || "-"} />, []);

    const StatusCell = useCallback(({ row }) => <AppStatus status={row.values.status} />, []);

    //prettier-ignore
    const AttachmentCell = useCallback(({ row }) => {
        const file = row.original?.companyLeaveApplicationFiles?.[0];
        return (<AppTableCell center value={file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-")} />);
    }, []);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Employee ID",
            id: "employeeId",
            accessor: "employeeDetail.employeeId",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Name",
            id: "name",
            accessor: "employeeDetail.personalInfo.name",
            disableSortBy: false,
            className: "name-column",
            Cell: ({ row }) =>
                <div className="name-column">
                    <AppTableCell left value={row.original.employeeDetail.personalInfo.name} />
                </div>
            ,
        },
        {
            Header: "Nickname",
            id: "nickName",
            accessor: "employeeDetail.personalInfo.nickName",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Leave Type",
            id: "leaveType",
            accessor: "leaveType",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Days",
            id: "noOfDays",
            accessor: "noOfDays",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Start Date",
            id: "startDate",
            accessor: "companyLeaveApplicationDetails",
            disableSortBy: false,
            Cell: StartDateCell
        },
        {
            Header: "End Date",
            id: "endDate",
            accessor: "companyLeaveApplicationDetails",
            disableSortBy: false,
            Cell: EndDateCell
        },
        {
            Header: "Time Off",
            id: "timeOff",
            accessor: "timeOff.label",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Attachment",
            id: "attachment",
            accessor: "companyLeaveApplicationFiles",
            disableSortBy: true,
            Cell: AttachmentCell
        },
        {
            Header: "Status",
            id: "status",
            accessor: "status",
            disableSortBy: false,
            Cell: StatusCell,
        },
        {
            Header: "Reject Reason",
            id: "rejectReason",
            accessor: "rejectReason",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Remarks",
            id: "remarks",
            accessor: "remarks",
            disableSortBy: false,
            Cell: ({ value }) => value || "-",
        },
        {
            Header: "Last Update by",
            id: "lastModifiedDate",
            accessor: "lastModifiedDate",
            disableSortBy: false,
            Cell: ({ row }) => <AppTableCell left value={`${row.original.lastModifiedByName || "-"}\n${formatDateTime(new Date(row.original.lastModifiedDate))}`} />,
        },
        {
            Header: "",
            id: "",
            accessor: "*",
            disableSortBy: true,
            Cell: MenuCell,
        },
    ], [MenuCell, StatusCell, StartDateCell, EndDateCell, AttachmentCell]);
    
    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    // opens the detail modal when arriving from a notification, then strips the
    // param so the same notification can be clicked again.
    useEffect(() => {
        const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

        if (!referenceId) return;

        notificationRefId.current = "";

        viewRef.current?.onHandleShow(referenceId);

        memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
    }, [searchParams]);

    useEffect(() => {
        const { page, sort, ...res } = paramsRef.current;

        setValues((prev) => ({ ...prev, ...res }));
    }, [setValues]);

    useEffect(() => {
        return () => {
            context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.LEAVES.LIST);
        };
    }, [context]);

    const onHandlePendingApproval = useCallback(() => {
        navigate(pathnames.leaveManagement.leavesApproval);
    }, [navigate]);

    const [selectedColumnIds, setSelectedColumnIds] = useState(() =>
        columns.map((col) => col.id).filter(col => defaultNoDisplayHeaders(col))
    );

    const [exportColumnIds, setExportColumnIds] = useState(() =>
        columns.map((col) => col.id)
    );

    useEffect(() => {
        setExportColumnIds(selectedColumnIds);
    }, [selectedColumnIds]);

    const visibleColumns = useMemo(() => {
        return columns.filter((col) => selectedColumnIds.includes(col.id));
    }, [selectedColumnIds, columns]);

    const toggleColumn = (id) => {
        setSelectedColumnIds((prev) => {
            if (!prev.includes(id)) return [...prev, id];

            const next = prev.filter((colId) => colId !== id);
            const hasDataColumn = next.some((colId) => colId && colId !== "*" && colId !== "**");

            return hasDataColumn ? next : prev;
        });
    };

    const onHandleExport = useCallback(async () => {
        setIsExporting(true);
        let response = null;
        let fileName = null;

        const values = { ...paramsRef.current };
        const visibleHeader = columns.filter((col) => exportColumnIds.includes(col.id)).map((col) => col.Header);

        let sortFilter = values.sort;
        if (sortFilter) {
            const [sortColumn] = sortFilter.split(",");
            if (!exportColumnIds.includes(sortColumn)) {
                sortFilter = null;
            }
        }

        try {
            const payload = {
                filters: {
                    name: values.name,
                    nickName: values.nickName,
                    employeeId: values.employeeId,
                    email: values.email,
                    statuses: values.statusListStr,
                    leaveType: values.leaveType,
                    startDate: values.startDate,
                    endDate: values.endDate,
                    createdStartDate: values.createdStartDate,
                    createdEndDate: values.createdEndDate,
                },
                filteredColumn: exportColumnIds.filter(id => id !== "" && id !== null && id !== undefined),
                filteredColumnHeader: visibleHeader,
                sort: sortFilter,
            };

            const transformResponse = (data, headers) => {
                fileName = headers?.["content-disposition"]?.split("attachment; filename=")?.[1];

                if (fileName) return data;

                try {
                    const jsonResponse = JSON.parse(new TextDecoder().decode(data));
                    if (jsonResponse) return jsonResponse;
                } catch (error) {
                    return data;
                }
            };

            response = await api.post.leaves.export(payload, { transformResponse });
        } catch (error) {
            serveRequestErrors(error);
        } finally {
            setIsExporting(false);
        }

        if (response) {
            const a = document.createElement("a");
            document.body.appendChild(a);
            const url = window.URL.createObjectURL(new Blob([response]), { type: "application/octet-stream" });
            a.href = url;
            a.download = fileName;
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 1000);
        }
    }, [columns, exportColumnIds]);

    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
            { label: "Export", icon: exportIcon, outline: true, onClick: onHandleExport, disabled: isExporting, showLoadingDots: isExporting },
            { label: "Pending Approval", outline: false, onClick: onHandlePendingApproval },
        ];
        return buttons;
    }, [onHandlePendingApproval, onHandleExport, isExporting]);

    function defaultNoDisplayHeaders(col) {
        if (col === "employeeId") return false;
        if (col === "nickName") return false;
        if (col === "rejectReason") return false;
        if (col === "remarks") return false;
        return true;
    }

    return (
        <Fragment>
            {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}

            {!isEmptyState && (
                <Fragment>
                    <AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
                        <form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
                            <div className="advance-form">
                                <div className="advance-form__inputs">
                                    <span className="advance-form__inputs__toggle-word">Select Columns to Display:</span>
                                    <div className="advance-form__inputs__checkbox">
                                        {columns
                                            .filter((col) => col.id !== "")
                                            .map((col) => (
                                                <AppCheckbox
                                                    key={col.id}
                                                    name={col.id}
                                                    label={col.Header}
                                                    value={selectedColumnIds.includes(col.id)}
                                                    onClick={() => toggleColumn(col.id)}
                                                />
                                            ))}
                                    </div>

                                    <AppInput type="text" name="employeeId" label="Employee ID" placeholder="Enter employee ID" value={formik.values.employeeId} onChange={formik.handleChange} />

                                    <AppInput type="text" name="name" label="Employee Name" placeholder="Enter employee name" value={formik.values.name} onChange={formik.handleChange} />

                                    <AppInput type="text" name="nickName" label="Nickname" placeholder="Enter nickname" value={formik.values.nickName} onChange={formik.handleChange} />

                                    <AppInput type="text" name="email" label="Employee Email" placeholder="Enter employee email" value={formik.values.email} onChange={formik.handleChange} />

                                    <AppSelectInput name="statusListStr" label="Status" placeholder="Select Status" options={COMMON.DROPDOWNS.LEAVES_HISTORY_STATUS} value={formik.values.statusListStr} onChange={formik.handleChange} />

                                    <AppSelectInput name="leaveType" label="Leave Type" placeholder="Select Type" options={COMMON.DROPDOWNS.LEAVES_TYPE} value={formik.values.leaveType} onChange={formik.handleChange} />
                                </div>

                                <div className="advance-form__inputs advance-form__leave_history_search">
                                    <AppCalendarInput name="startDate" label="Leave Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} onChange={onHandleStartDate} />

                                    <AppCalendarInput name="endDate" label="Leave End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.startDate} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} onChange={formik.setFieldValue} />

                                    <AppCalendarInput name="createdStartDate" label="Submission Date From" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} value={formik.values.createdStartDate} error={formik.errors.createdStartDate} touched={formik.touched.createdStartDate} onChange={onHandleCreatedStartDate} />

                                    <AppCalendarInput name="createdEndDate" label="Submission Date To" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.createdStartDate} value={formik.values.createdEndDate} error={formik.errors.createdEndDate} touched={formik.touched.createdEndDate} onChange={formik.setFieldValue} />
                                </div>

                                <div className="advance-form__button-container">
                                    <AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
                                    <AppButton type="submit" label="Search" />
                                </div>
                            </div>
                        </form>
                    </AppSearchInput>

                    {(paramsRef.current.employeeId || paramsRef.current.statusListStr || paramsRef.current.name
                        || paramsRef.current.nickName || paramsRef.current.email || paramsRef.current.leaveType 
                        || paramsRef.current.startDate || paramsRef.current.endDate || paramsRef.current.createdStartDate 
                        || paramsRef.current.createdEndDate
                    ) && (
                        <div className="leave-history__results">
                            <p className="leave-history__text">{data.totalElements} results found</p>
                        </div>
                    )}

                    <AppTable ref={tableRef} columns={visibleColumns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                </Fragment>
            )}

            <Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem onClick={onHandleView}><img className="app-table-menu__icon" src={viewIcon} alt="view" />View</MenuItem>
                {selectedLeave?.status === COMMON.STATUS_ID.APPROVED && (<MenuItem onClick={onHandleRemove}><img className="app-table-menu__icon" src={removeIcon} alt="view" />Cancel</MenuItem>)}
            </Menu>

            <AppLeaveModal ref={viewRef}/>
            <AppConfirmDeleteModal ref={confirmDeleteRef} onHandleGetList={onHandleGetList} />
        </Fragment>
    );
};

export default AppAllLeaveHistory;
